import {type KeyboardEvent, type MouseEvent, useCallback, useEffect, useRef, useState} from 'react';

const DEFAULT_CLOSE_DELAY_MS = 120;

/**
 * Hover-bridge popover state: opens on hover/focus of the trigger and stays open while the cursor is
 * over the trigger OR the popover content, so the user can cross the gap to click inside it (a plain
 * tooltip can't — its portaled content has no keep-open handlers). Spread `triggerProps` on the
 * trigger and `contentProps` on the popover content; pass `triggerRef` to the Popover's `targetRef`.
 */
export const useHoverPopover = (closeDelayMs: number = DEFAULT_CLOSE_DELAY_MS) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const open = useCallback(() => {
    cancelClose();
    setIsOpen(true);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), closeDelayMs);
  }, [cancelClose, closeDelayMs]);

  useEffect(() => cancelClose, [cancelClose]);

  return {
    isOpen,
    triggerRef,
    triggerProps: {
      ref: triggerRef,
      'aria-expanded': isOpen,
      onMouseEnter: open,
      onMouseLeave: scheduleClose,
      onFocus: open,
      onBlur: scheduleClose,
      onClick: () => setIsOpen((prev) => !prev),
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      }
    },
    contentProps: {
      onMouseEnter: cancelClose,
      onMouseLeave: scheduleClose,
      onMouseDown: (event: MouseEvent) => event.stopPropagation()
    }
  };
};
